import type { ReactNode } from 'react';
import { cloneElement, isValidElement } from 'react';
import Button from '@components/Button';
import IconMdi from '@components/IconMdi';
import { Show } from '@components/Show';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { mdiChevronDown, mdiChevronUp, mdiFilter, mdiViewColumn } from '@mdi/js';
import { GlobalSearch } from '@pages/User/components/GlobalSearch';
import { useListingPagesStore } from '@pages/User/store/listingPages.store';
import { type Action, isPlainAction } from '@root/@types/types';
import type { Table } from '@tanstack/react-table';
import clsx from 'clsx';
import pluralize from 'pluralize';

import { ActionButton } from './ActionButton';
import { ColumnVisibilityControls } from './ColumnVisibilityControls';

interface ActionBarProps {
  actions: Action[];
  activeFiltersCount: number;
  enableColumnFilters: boolean;
  entityName: string;
  handleGlobalSearchClick?: (searchTerm: string) => void;
  isColumnVisibilityControlsVisible: boolean;
  isFiltersBarVisible: boolean;
  renderCustomElements?: () => ReactNode;
  searchTerm?: string;
  table: Table<any>;
  toggleColumnVisibilityControls?: () => void;
  toggleFiltersBarVisibility?: () => void;
}

export const ActionsBar = ({
  actions,
  activeFiltersCount,
  enableColumnFilters,
  entityName,
  handleGlobalSearchClick,
  isColumnVisibilityControlsVisible,
  isFiltersBarVisible,
  renderCustomElements,
  searchTerm,
  table,
  toggleColumnVisibilityControls,
  toggleFiltersBarVisibility,
}: ActionBarProps) => {
  const entitiesLabel = pluralize(entityName);

  const { initialSearchPhrase } = useListingPagesStore(({ listingPagesState }) => ({
    initialSearchPhrase: listingPagesState?.[entityName]?.globalFilter,
  }));

  return (
    <div className="flex items-center justify-between gap-x-2">
      <div className="flex flex-auto shrink-0 items-center self-start sm:mb-4 lg:mb-0">
        {actions?.map((action, index) => {
          if (isPlainAction(action)) {
            return <ActionButton action={action} key={action.key} />;
          }

          if (isValidElement(action)) {
            return cloneElement(action, { key: action.key || index });
          }

          return null;
        })}
      </div>
      {renderCustomElements?.()}

      <Show when={!!toggleColumnVisibilityControls}>
        <Popover open={isColumnVisibilityControlsVisible} onOpenChange={toggleColumnVisibilityControls}>
          <PopoverTrigger asChild>
            <Button
              kind="secondary"
              className={clsx(
                'mr-2 inline-flex h-[38px] items-center rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50',
                isColumnVisibilityControlsVisible && 'border-primary-600 bg-info-50',
              )}
              type="button"
              data-testid="toggle:columns-visibility"
            >
              <IconMdi className="text-gray-400" path={mdiViewColumn} />
              Columns
              <IconMdi path={mdiChevronDown} />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="bg-info-50 w-64">
            <ColumnVisibilityControls table={table} onClose={toggleColumnVisibilityControls} />
          </PopoverContent>
        </Popover>
      </Show>

      <Show when={enableColumnFilters && toggleFiltersBarVisibility !== undefined}>
        <button
          className={clsx(
            'border-info-300 hover:bg-info-100 flex items-center justify-between border bg-white p-2 md:rounded-lg',
            isFiltersBarVisible && 'border-primary-600 !bg-info-50 border',
          )}
          onClick={toggleFiltersBarVisibility}
          data-testid="toggle:filters-bar-visibility"
        >
          <div className="flex items-center gap-2">
            <IconMdi path={mdiFilter} className="text-gray-400" />
            <span className="text-sm leading-5 font-medium text-gray-700">Filters</span>
            {activeFiltersCount > 0 && (
              <span className="bg-primary-600 inline-flex h-5 w-5 items-center justify-center rounded-full text-xs leading-5 font-medium text-white">
                {activeFiltersCount}
              </span>
            )}
            <IconMdi className="ml-auto" path={isFiltersBarVisible ? mdiChevronUp : mdiChevronDown} />
          </div>
        </button>
      </Show>

      <Show when={handleGlobalSearchClick !== undefined}>
        <div className="ml-2 w-64">
          <GlobalSearch
            entitiesLabel={entitiesLabel}
            initialSearchPhrase={initialSearchPhrase}
            onSearchClick={handleGlobalSearchClick!}
            searchPhrase={searchTerm}
          />
        </div>
      </Show>
    </div>
  );
};
